import React, { useRef } from "react";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ClearIcon from "@mui/icons-material/Clear";

/**
 * Image field that allows either a URL or a local file (mutually exclusive).
 *
 * Props:
 *   fieldName  string           — display label
 *   urlValue   string           — current URL value (empty string when a file is selected)
 *   pendingFile File|null       — pending File object (null when a URL is typed)
 *   previewUrl string|null      — blob URL for local preview
 *   onUrlChange (url) => void   — called when the URL text field changes
 *   onFileChange (file, previewUrl) => void — called with File + blob preview URL
 *   onClearFile () => void      — called when the file chip is dismissed
 */
export default function ImageFieldInput({
    fieldName,
    urlValue,
    pendingFile,
    previewUrl,
    onUrlChange,
    onFileChange,
    onClearFile,
}) {
    const fileInputRef = useRef(null);

    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const blob = URL.createObjectURL(file);
        onFileChange(file, blob);
    };

    return (
        <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
                {fieldName} (image)
            </Typography>

            {/* URL input — hidden when a file is pending */}
            {!pendingFile && (
                <TextField
                    size="small"
                    fullWidth
                    label="URL de l'image"
                    placeholder="https://..."
                    value={urlValue}
                    onChange={(e) => onUrlChange(e.target.value)}
                />
            )}

            {/* File chip shown when a file is pending */}
            {pendingFile && (
                <Chip
                    icon={<AttachFileIcon fontSize="small" />}
                    label={pendingFile.name}
                    onDelete={onClearFile}
                    deleteIcon={<ClearIcon fontSize="small" />}
                    size="small"
                    sx={{ maxWidth: "100%", justifyContent: "flex-start" }}
                />
            )}

            {/* Thumbnail */}
            {(previewUrl || urlValue) && (
                <Box
                    component="img"
                    src={previewUrl || urlValue}
                    alt=""
                    sx={{
                        width: "100%",
                        maxHeight: 90,
                        objectFit: "contain",
                        borderRadius: 1,
                        bgcolor: "#f0f0f0",
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                    onError={(e) => { e.target.style.display = "none"; }}
                    onLoad={(e) => { e.target.style.display = ""; }}
                />
            )}

            <Button
                size="small"
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
            >
                {pendingFile ? "Remplacer le fichier" : "Importer un fichier"}
            </Button>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: "none" }}
                onChange={handleFileSelected}
            />
        </Stack>
    );
}
